// normalizeStyle.js
const UNSUPPORTED = [
  'writingDirection',
  'includeFontPadding',
  'textAlignVertical'
  // text-shadow props are now handled below, so remove them from unsupported
  // elevation → boxShadow mapping covers basic Android shadows
]

// main normalizer
export const normalizeStyle = (style) => {
  if (!style || typeof style !== 'object') return style
  const s = { ...style }

  // remove unsupported
  UNSUPPORTED.forEach((k) => delete s[k])

  // fontWeight number → string
  if (typeof s.fontWeight === 'number') {
    s.fontWeight = String(s.fontWeight)
  }

  // fontFamily array → comma-list
  if (Array.isArray(s.fontFamily)) {
    s.fontFamily = s.fontFamily.join(', ')
  }

  // flex: number → grow/shrink/basis
  if ('flex' in s && typeof s.flex === 'number') {
    s.flexGrow = s.flex
    s.flexShrink = 1
    s.flexBasis = '0%'
    delete s.flex
  }

  // margin/padding start/end (for RTL support) → left/right
  const margins = [
    ['marginStart', 'marginLeft'],
    ['marginEnd', 'marginRight'],
    ['paddingStart', 'paddingLeft'],
    ['paddingEnd', 'paddingRight']
  ]

  margins.forEach(([prop, cssProp]) => {
    if (prop in s) {
      s[cssProp] = s[prop]
      delete s[prop]
    }
  })

  // padding/margin horizontal & vertical
  const paddings = [
    ['paddingHorizontal', ['paddingLeft', 'paddingRight']],
    ['paddingVertical', ['paddingTop', 'paddingBottom']],
    ['marginHorizontal', ['marginLeft', 'marginRight']],
    ['marginVertical', ['marginTop', 'marginBottom']]
  ]

  paddings.forEach(([prop, [a, b]]) => {
    if (prop in s) {
      s[a] = s[prop]
      s[b] = s[prop]
      delete s[prop]
    }
  })

  // aspectRatio → CSS aspect-ratio
  if ('aspectRatio' in s) {
    s.aspectRatio = String(s.aspectRatio)
  }

  // borderWidth shorthand → retains px
  if ('borderWidth' in s) {
    s.borderStyle = s.borderStyle || 'solid'
    s.borderColor = s.borderColor || 'black'
  }

  // borderRadius and individual corners pass through

  // textDecorationLine → textDecoration
  if ('textDecorationLine' in s) {
    s.textDecoration = s.textDecorationLine
    delete s.textDecorationLine
  }

  // transform array → CSS transform string
  if (Array.isArray(s.transform)) {
    const parts = s.transform.map((t) => {
      const key = Object.keys(t)[0]
      let val = t[key]
      // append px for numeric translate values
      if (
        (key === 'translateX' || key === 'translateY' || key === 'translate') &&
        typeof val === 'number'
      ) {
        val = `${val}px`
      }
      return `${key}(${val})`
    })
    s.transform = parts.join(' ')
  }

  // shadow props → boxShadow
  if ('elevation' in s && typeof s.elevation === 'number') {
    const e = s.elevation
    // simple Android elevation mapping
    s.boxShadow = `0px ${e}px ${e * 2}px rgba(0,0,0,0.24)`
    delete s.elevation
  }

  if (
    'shadowColor' in s ||
    'shadowOffset' in s ||
    'shadowOpacity' in s ||
    'shadowRadius' in s
  ) {
    const color = s.shadowColor || 'black'
    const { width = 0, height = 0 } = s.shadowOffset || {}
    const radius = s.shadowRadius || 0
    const opacity = s.shadowOpacity != null ? s.shadowOpacity : 1

    s.boxShadow = `${width}px ${height}px ${radius}px rgba(${color}, ${opacity})`

    delete s.shadowColor
    delete s.shadowOffset
    delete s.shadowRadius
    delete s.shadowOpacity
  }

  const usesFlex =
    'flexDirection' in s ||
    'justifyContent' in s ||
    'alignItems' in s ||
    'alignContent' in s ||
    'flexWrap' in s ||
    'flexGrow' in s ||
    'flexShrink' in s ||
    'flexBasis' in s

  if (usesFlex && !s.display) {
    s.display = 'flex'
  }

  return s
}
